import { useEffect, useMemo, useRef, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import InputField from "../../../components/InputField";
import SearchIcon from "../../../assets/SearchIcon";
import ManagementTableLayout from "../../../components/MangementTable";
import Pagination from "../../../components/Pagination";
import useDebounce from "../../../hooks/useDebounce";
import UpArrow2 from "../../../assets/svg-tsx/UpArrow2";
import EyeIcon from "../../../assets/svg-tsx/EyeIcon";
import SelectField2 from "../../../components/SelectedField2";
import FilterIcon from "../../../assets/svg-tsx/FilterIcon";
import { getBriefList } from "../../../api/briefManagement";
import { formatDate } from "../../../utils/helper";

// Status → display label + uniform pill classes.
// All pills share the same width / weight so the column scans cleanly.
const STATUS_DISPLAY: Record<
  string,
  { label: string; className: string }
> = {
  PENDING: {
    label: "IN-REVIEW",
    className: "bg-[#FFDF8680] text-[#7A5800]",
  },
  APPROVED: {
    label: "ACTIVE",
    className: "bg-[#04B36B33] text-[#0A6B43]",
  },
  REJECTED: {
    label: "REJECTED",
    className: "bg-[#EA191933] text-[#A61414]",
  },
  EXPIRED: {
    label: "EXPIRED",
    className: "bg-[#E3E3E3] text-[#5E5E5E]",
  },
};

const STATUS_FILTER_OPTIONS = [
  { value: "PENDING", label: "In Review" },
  { value: "APPROVED", label: "Active" },
  { value: "REJECTED", label: "Rejected" },
  { value: "EXPIRED", label: "Expired" },
];

const formatLocation = (city?: string, state?: string) =>
  [city, state].filter(Boolean).join(", ") || "-";

const formatArea = (area?: string | number | null) => {
  if (area === null || area === undefined || area === "") return "-";
  const num = typeof area === "string" ? Number(area) : area;
  if (Number.isNaN(num)) return `${area} sq ft`;
  // Drop trailing .00 for cleaner scan.
  const clean = Number.isInteger(num) ? num : Number(num.toFixed(2));
  return `${clean.toLocaleString("en-IN")} sq ft`;
};

function BriefTable() {
  const [briefList, setBriefList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState<number>(1);
  const [pageLength, setPageLength] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [startIndex, setStartIndex] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("");

  const debouncedSearchValue = useDebounce(searchValue, 500);

  // Each fetch claims a sequence number; only the latest in-flight request's
  // response is applied. Prevents stale responses from clobbering newer state
  // when the user types/filters quickly.
  const requestSeqRef = useRef(0);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      setSortBy("");
    } else {
      setSortBy(field);
    }
  };

  const getSortDirection = (field: string) => {
    if (sortBy === field) return "asc";
    if (sortBy === `-${field}`) return "desc";
    return "none";
  };

  useEffect(() => {
    const mySeq = ++requestSeqRef.current;
    setIsLoading(true);
    const params: Record<string, string | number> = {
      page,
      length: pageLength,
    };
    if (debouncedSearchValue) params.search = debouncedSearchValue;
    if (statusFilter) params.status = statusFilter;
    if (sortBy) params.sort_by = sortBy;

    getBriefList(params)
      .then((res: any) => {
        if (mySeq !== requestSeqRef.current) return;
        const rows = res?.data?.briefs || [];
        const total =
          res?.data?.pagination?.total_count ?? rows.length ?? 0;
        setBriefList(rows);
        setTotalCount(total);
      })
      .catch(() => {
        if (mySeq !== requestSeqRef.current) return;
        setBriefList([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (mySeq === requestSeqRef.current) setIsLoading(false);
      });
  }, [page, pageLength, debouncedSearchValue, statusFilter, sortBy]);

  useEffect(() => {
    const lastIndex = (page - 1) * pageLength;
    setStartIndex(lastIndex);
  }, [page, pageLength, totalCount]);

  const COLUMN = [
    {
      id: "sr",
      Header: () => <div className="text-[12px] text-[#7B7B7B] font-gilroy-medium">#</div>,
      accessor: "sr",
      width: "4%",
      Cell: ({ row }: { row: any }) => (
        <div className="text-[12px] text-[#7B7B7B] font-gilroy-medium">
          {startIndex + row.index + 1}
        </div>
      ),
    },
    {
      id: "created_at",
      Header: () => (
        <div
          className="flex justify-start items-center gap-2 cursor-pointer"
          onClick={() => handleSort("created_at")}
        >
          Posted
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("created_at") === "desc"
                  ? "rotate-180"
                  : getSortDirection("created_at") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      accessor: "created_at",
      width: "10%",
      Cell: ({ value }: any) => (
        <div className="py-3 text-[13px] text-pot-grey5 font-gilroy-medium">
          {formatDate(value)}
        </div>
      ),
    },
    {
      id: "title",
      Header: () => <div>Brief</div>,
      accessor: "title",
      width: "26%",
      Cell: ({ row }: any) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/briefs/${row?.original?.id}`}
              className="font-gilroy-semi-bold text-[13.5px] text-pot-grey5 hover:text-pot-yellow truncate max-w-[200px] transition-colors"
              title={row.original.title}
            >
              {row.original.title || "Untitled brief"}
            </Link>
            {row.original.extension_count > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[9.5px] font-gilroy-semi-bold bg-[#E8F1FF] text-[#1759AC] tracking-wide whitespace-nowrap uppercase">
                Ext × {row.original.extension_count}
              </span>
            )}
          </div>
          {(row.original.urgency || row.original.project_type) && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#7B7B7B] font-gilroy-medium">
              {row.original.project_type && (
                <span>{row.original.project_type}</span>
              )}
              {row.original.project_type && row.original.urgency && (
                <span className="text-[#D8D8D8]">•</span>
              )}
              {row.original.urgency && (
                <span>{row.original.urgency}</span>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "contact_name",
      Header: () => <div>Posted by</div>,
      accessor: "contact_name",
      width: "13%",
      Cell: ({ value }: any) => (
        <div
          className="text-[13px] text-pot-grey5 font-gilroy-medium truncate max-w-[140px]"
          title={value}
        >
          {value || "-"}
        </div>
      ),
    },
    {
      id: "location",
      Header: () => <div>Location</div>,
      accessor: "location",
      width: "14%",
      Cell: ({ row }: any) => (
        <div
          className="text-[13px] text-pot-grey5 font-gilroy-medium truncate max-w-[160px]"
          title={formatLocation(row.original.city, row.original.state)}
        >
          {formatLocation(row.original.city, row.original.state)}
        </div>
      ),
    },
    {
      id: "area",
      Header: () => <div>Area</div>,
      accessor: "project_area",
      width: "10%",
      Cell: ({ value }: any) => (
        <div className="text-[13px] text-pot-grey5 font-gilroy-medium">
          {formatArea(value)}
        </div>
      ),
    },
    {
      id: "interested_count",
      Header: () => <div className="text-center">Interested</div>,
      accessor: "interested_count",
      width: "8%",
      Cell: ({ value }: any) => (
        <div className="text-center">
          <span className="inline-flex items-center justify-center min-w-[24px] h-[20px] px-1.5 rounded-md bg-pot-grey1 text-pot-grey5 text-[12px] font-gilroy-semi-bold">
            {value ?? 0}
          </span>
        </div>
      ),
    },
    {
      id: "status",
      Header: () => <div>Status</div>,
      accessor: "status",
      width: "10%",
      Cell: ({ value }: any) => {
        const display = STATUS_DISPLAY[value] || STATUS_DISPLAY.PENDING;
        return (
          <span
            className={`inline-flex items-center justify-center w-[88px] h-[22px] rounded-md text-[10.5px] font-gilroy-semi-bold tracking-[0.4px] ${display.className}`}
          >
            {display.label}
          </span>
        );
      },
    },
    {
      id: "actions",
      Header: () => <div className="text-center">Action</div>,
      accessor: "actions",
      width: "5%",
      Cell: ({ row }: any) => (
        <div className="flex gap-2 justify-center items-center">
          <Tooltip title="View brief">
            <Link
              to={`/briefs/${row?.original?.id}`}
              className="hover:opacity-70 inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-pot-grey1 transition-colors"
            >
              <EyeIcon size="14" />
            </Link>
          </Tooltip>
        </div>
      ),
    },
  ];

  const columns = useMemo(() => COLUMN, [briefList, sortBy, startIndex]);
  const data = useMemo(() => briefList || [], [briefList]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: page, pageSize: pageLength },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageLength),
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar — search + count + filter, all same row, sleek visual rhythm */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-c-white-3">
        <div className="w-[280px]">
          <InputField
            onChange={(e: string) => {
              setSearchValue(e);
              if (page !== 1) {
                setPage(1);
              }
            }}
            placeholder="Search title, posted by, city"
            value={searchValue}
            rightIcon={<SearchIcon />}
          />
        </div>

        {/* Tiny inline result count — gives admins context without taking room */}
        <div className="text-[12px] text-[#7B7B7B] font-gilroy-medium">
          {!isLoading && (
            <>
              {totalCount === 0
                ? "No briefs"
                : `${totalCount} ${totalCount === 1 ? "brief" : "briefs"}`}
              {(statusFilter || debouncedSearchValue) && (
                <span className="ml-1">matching</span>
              )}
            </>
          )}
        </div>

        <div className="ml-auto w-[200px]">
          <SelectField2
            disabled={isLoading}
            placeholder="All statuses"
            value={statusFilter}
            onChange={(e: string) => {
              if (e === statusFilter) {
                setStatusFilter("");
              } else {
                setStatusFilter(e);
              }
              setPage(1);
            }}
            removeOption={() => {
              setStatusFilter("");
              setPage(1);
            }}
            options={STATUS_FILTER_OPTIONS}
            leftIcon={<FilterIcon />}
          />
        </div>
      </div>

      <div className="flex-1 w-full relative -z-0">
        <ManagementTableLayout tableInstance={tableInstance} loading={isLoading} />
      </div>

      {isLoading ? null : (
        <div className="w-full">
          <Pagination
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageLength}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageLength}
          />
        </div>
      )}
    </div>
  );
}

export default BriefTable;
